/**
 * The question a new organisation's admin is asked once: what will you use
 * Rowstorm for first? Each answer is a template (`routes/v1/templates.rs`):
 * it makes a first stream with what that stream needs. "We will create"
 * lists exactly what applying it makes, as the server says. Every
 * organisation reads the same words, so no answer changes them.
 *
 * Shown only while the server says nobody has answered
 * (`GET /api/v1/settings/vocabulary` returns `chosen: false`), and only to
 * an admin or super admin of the organisation itself, the people who can
 * apply one. Every organisation that existed before the question was
 * recorded as having answered, so none of them ever sees it, and the server
 * refuses a sign-up template for them anyway. Everyone else in a new
 * organisation sees nothing.
 *
 * An organisation that already has streams (set up by Hawfinch before its
 * admin signed in) gets nothing new made: the picker says so.
 *
 * "Decide later" puts it away for this browser session. Signing in again
 * clears that (see AuthProvider), so it comes back until a choice is saved.
 * Closing it any other way counts as "later" too: nobody is ever stuck here.
 */
import { useState } from 'react';
import { Badge, Button, Group, Modal, Radio, SimpleGrid, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useVocabularySetting } from '../api/queries';
import { useApplyTemplate, useTemplates, type TemplateSummary } from '../api/templates';
import { VOCAB_CHOICE_LATER_KEY, useAuth } from '../auth/AuthProvider';
import type { GatewayUser } from '../api/auth';
import { TOKENS } from '../theme';
import { useVocab } from '../vocab';

/** Lead generation is the front door; the picker starts there. */
const SUGGESTED = 'lead_generation';

function laterThisSession(): boolean {
  try {
    return sessionStorage.getItem(VOCAB_CHOICE_LATER_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberLater() {
  try {
    sessionStorage.setItem(VOCAB_CHOICE_LATER_KEY, '1');
  } catch {
    /* private mode: it just shows again on the next page load */
  }
}

/**
 * Admins and super admins of the organisation itself. Buyer and supplier
 * logins carry a scope and never answer for the organisation, and an
 * unapproved person has the `unapproved` role, so none of them qualify.
 */
function canChoose(user: GatewayUser | null): boolean {
  if (!user) return false;
  if (user.role !== 'admin' && user.role !== 'super_admin') return false;
  return user.allowed_buyer_ids == null && user.allowed_supplier_ids == null;
}

/** "We will create": the chips, and what is left to the person. */
export function WeWillCreate({
  template,
  hasStreams,
}: {
  template: TemplateSummary;
  hasStreams: boolean;
}) {
  const v = useVocab();
  return (
    <div
      data-testid="we-will-create"
      style={{
        background: TOKENS.surface2,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 10,
        padding: '10px 14px',
      }}
    >
      <Stack gap={8}>
        <Text size="sm" fw={600}>We will create</Text>
        {hasStreams ? (
          <Text size="sm" c="dimmed">
            {`Your organisation already has ${v.many('campaign')}, so nothing new is made.`}
          </Text>
        ) : (
          <Group gap={6} wrap="wrap">
            {template.creates.map((c) => (
              <Badge key={c} variant="default" radius="xl" tt="none" size="lg" fw={500}>
                {c}
              </Badge>
            ))}
          </Group>
        )}
        {!hasStreams && template.next_step && (
          <Text size="xs" c="dimmed">{template.next_step}</Text>
        )}
      </Stack>
    </div>
  );
}

export function TemplateOnboarding() {
  const { user } = useAuth();
  const eligible = canChoose(user);
  // Only `chosen` is read: whether anyone has answered yet.
  const setting = useVocabularySetting(eligible);
  const asking = eligible && setting.data?.chosen === false;
  const templates = useTemplates(asking);
  const apply = useApplyTemplate();
  const navigate = useNavigate();
  const v = useVocab();
  const [later, setLater] = useState(laterThisSession);
  const [choice, setChoice] = useState<string>(SUGGESTED);
  const saving = apply.isPending;

  const list = templates.data?.templates ?? [];
  const opened = asking && !later && list.length > 0;
  const picked = list.find((t) => t.key === choice) ?? list[0];
  const hasStreams = templates.data?.has_streams ?? false;

  const decideLater = () => {
    rememberLater();
    setLater(true);
  };

  const confirm = () => {
    if (!picked) return;
    apply.mutate(
      { key: picked.key, onboarding: true },
      {
        onSuccess: (r) => {
          notifications.show({
            color: 'teal',
            title: 'All set',
            message: r.campaign_id
              ? `${picked.name} is set up.${r.next_step ? ` ${r.next_step}` : ''}`
              : `Nothing new was made: your organisation already has ${v.many('campaign')}.`,
            autoClose: r.next_step ? 10000 : 4000,
          });
          if (r.campaign_id) navigate(`/campaigns/${encodeURIComponent(r.campaign_id)}`);
        },
        onError: (e) =>
          notifications.show({ color: 'red', title: 'Could not set it up', message: (e as Error).message }),
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={decideLater}
      size="xl"
      centered
      title={
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.06em' }}>
            New organisation
          </Text>
          <Text fw={700} size="lg">What will you use Rowstorm for first?</Text>
        </div>
      }
    >
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          This sets up your first stream with what it needs. Everything else stays
          available, and you can add more from Settings › Templates.
        </Text>

        <Radio.Group
          value={picked?.key ?? choice}
          onChange={setChoice}
          aria-label="What will you use Rowstorm for first?"
        >
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {list.map((t) => (
              <Radio.Card
                key={t.key}
                value={t.key}
                radius="md"
                p="md"
                style={{
                  borderColor: picked?.key === t.key ? TOKENS.accent : TOKENS.border,
                  background: TOKENS.surface,
                }}
              >
                <Group wrap="nowrap" align="flex-start" gap="sm">
                  <Radio.Indicator mt={2} />
                  <div>
                    <Text fw={600} size="sm">{t.name}</Text>
                    <Text size="xs" c="dimmed">{t.description}</Text>
                  </div>
                </Group>
              </Radio.Card>
            ))}
          </SimpleGrid>
        </Radio.Group>

        {picked && <WeWillCreate template={picked} hasStreams={hasStreams} />}

        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={decideLater} disabled={saving}>
            Decide later
          </Button>
          <Button onClick={confirm} loading={saving} disabled={!picked} data-autofocus>
            Set it up
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
